/**
 * Admin de TA → Hiring managers (Sol 28/09/2026): quién puede abrir una
 * búsqueda ("Nueva búsqueda") además del roster de TA y los admins, que
 * siempre pueden. Tres criterios que se suman: el interruptor de líderes con
 * reportes directos en el community map (el original del 25/09), personas y
 * teams/áreas. Se guarda en TA_INTAKE_GOVERNANCE (lib/ta-access.ts).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { MemberPickerDialog } from '@/components/governance/MemberPickerDialog';
import { RosterTable } from '@/components/governance/RosterTable';
import { useSaveTaIntakeGovernance, useTaCommunityPicker, useTaIntakeGovernance } from '@/hooks/useTalentAcquisition';

export function TaHiringManagersCard() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.hiring_managers';
  // Columnas de la tabla y textos del picker: los mismos que Permisos
  const gk = 'talent_acquisition.governance';
  const gov = useTaIntakeGovernance();
  const { data: members = [], isLoading: membersLoading } = useTaCommunityPicker();
  const save = useSaveTaIntakeGovernance();

  const [leaders, setLeaders] = useState(true);
  const [managers, setManagers] = useState<string[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [picker, setPicker] = useState(false);
  const [addTeam, setAddTeam] = useState('');

  useEffect(() => {
    if (!gov.data) return;
    setLeaders(gov.data.leadersWithReports);
    setManagers(gov.data.managers);
    setTeams(gov.data.managerTeams);
  }, [gov.data]);

  const availableTeams = useMemo(() => {
    const set = new Set<string>();
    for (const m of members) { if (m.team) set.add(m.team); if (m.area) set.add(m.area); }
    for (const x of teams) set.add(x);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [members, teams]);
  const taken = useMemo(() => new Set(managers), [managers]);

  const dirty = !!gov.data && (
    leaders !== gov.data.leadersWithReports
    || managers.join('|') !== gov.data.managers.join('|')
    || teams.join('|') !== gov.data.managerTeams.join('|')
  );

  if (gov.isLoading) return <div className="flex justify-center py-8"><WinclapLoader /></div>;
  if (gov.isError || !gov.data) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-lg">{t(`${k}.title`)}</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-destructive">{t(`${k}.load_error`)}</p>
          <Button size="sm" variant="outline" onClick={() => gov.refetch()}>{t('talent_acquisition.rules.retry')}</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">{t(`${k}.title`)}</h2>
          <p className="text-sm text-muted-foreground">{t(`${k}.desc`)}</p>
        </div>
        <Button
          onClick={() => save.mutate({ leadersWithReports: leaders, managers, managerTeams: teams })}
          disabled={!dirty || save.isPending}
        >
          {save.isPending ? t(`${gk}.saving`) : t(`${gk}.save`)}
        </Button>
      </div>

      {/* Líderes con reportes directos */}
      <Card>
        <CardContent className="flex items-start justify-between gap-4 pt-6">
          <div>
            <p className="text-sm font-medium">{t(`${k}.leaders_title`)}</p>
            <p className="text-xs text-muted-foreground">{t(`${k}.leaders_desc`)}</p>
          </div>
          <Switch checked={leaders} onCheckedChange={setLeaders} aria-label={t(`${k}.leaders_title`)} />
        </CardContent>
      </Card>

      {/* Personas */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">{t(`${k}.people_title`)}</CardTitle>
            <CardDescription>{t(`${k}.people_desc`)}</CardDescription>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 gap-1.5" onClick={() => setPicker(true)}>
            <Plus className="h-3.5 w-3.5" /> {t(`${k}.add_person`)}
          </Button>
        </CardHeader>
        <CardContent>
          <RosterTable
            emails={managers}
            members={members}
            namespace={gk}
            empty={t(`${k}.people_empty`)}
            onRemove={(e) => setManagers((p) => p.filter((x) => x !== e))}
          />
        </CardContent>
      </Card>

      {/* Teams / áreas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t(`${k}.teams_title`)}</CardTitle>
          <CardDescription>{t(`${k}.teams_desc`)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {teams.length === 0 && <p className="text-sm text-muted-foreground">{t(`${k}.teams_empty`)}</p>}
            {teams.map((team) => (
              <Badge key={team} variant="secondary" className="gap-1.5 py-1 pl-2.5 pr-1 text-sm">
                {team}
                <button onClick={() => setTeams((p) => p.filter((x) => x !== team))} className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10" aria-label={t(`${gk}.remove`)}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex max-w-sm items-center gap-2">
            <Select value={addTeam} onValueChange={(v) => { if (v && !teams.includes(v)) setTeams((p) => [...p, v]); setAddTeam(''); }}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={membersLoading ? t(`${gk}.loading_teams`) : t(`${gk}.add_team`)} />
              </SelectTrigger>
              <SelectContent>
                {availableTeams.filter((x) => !teams.includes(x)).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">{t(`${k}.footer_note`)}</p>

      <MemberPickerDialog
        open={picker}
        onOpenChange={(o) => !o && setPicker(false)}
        title={t(`${k}.add_person`)}
        description={t(`${gk}.picker_desc`)}
        searchPlaceholder={t(`${gk}.picker_search`)}
        noResultsText={t(`${gk}.no_results`)}
        members={members}
        loading={membersLoading}
        exclude={taken}
        onPick={(m) => setManagers((p) => [...p, m.email.toLowerCase()])}
      />
    </div>
  );
}
